import {ScaleLinear} from 'd3';
import * as d3 from 'd3';

import {LayeringMode, AllPolymerLayouts, PolymerLayout} from 'constants/MapsConstants';
import BaseReactWrapperView, {BaseReactWrapperViewOptions} from 'molbio/maps/linear/BaseReactWrapperView';
import {getLayersDict, Layerable, LayersDict} from 'molbio/maps/linear/LayeringHelpers';

export interface LayeredReactWrapperViewOptions<
  TLayeringMode extends LayeringMode,
  M extends Layerable<TLayeringMode>,
  LayoutName extends keyof AllPolymerLayouts,
> extends BaseReactWrapperViewOptions<LayoutName> {
  layeringMode: TLayeringMode;
  singleHeight: () => number;
  getSliceModels: () => Array<M>;
  renderReact: (
    layout: AllPolymerLayouts[LayoutName],
    redraw: () => void,
    sliceModels: Array<M>,
    layersDict: LayersDict,
    layeringMode: LayeringMode,
    yFn: ScaleLinear<number, number>
  ) => JSX.Element;
}

/**
 * A ReactWrapperView extension for rendering layered / overlapping views such as primers and parts.
 *
 * boxHeight and _staticBoxHeight are ignored in this class; instead, we calculate box height as a multiple of
 * a single feature height.
 *
 * @param options.layeringMode How to layer features.
 * @param options.singleHeight A function to get the height of a single layer for this view, including any margins
 * @param options.getSliceModels A function to determine a list of RegionSelectable relevant to this slice,
 *   given the SliceViewModel
 */
export default class LayeredReactWrapperView<
  TLayeringMode extends LayeringMode,
  M extends Layerable<TLayeringMode>,
  LayoutName extends keyof AllPolymerLayouts,
> extends BaseReactWrapperView<LayoutName> {
  layeringMode!: TLayeringMode;
  singleHeight!: () => number;
  getSliceModels!: () => Array<M>;
  renderReact!: (
    layout: AllPolymerLayouts[LayoutName],
    redraw: () => void,
    sliceModels: Array<M>,
    layersDict: LayersDict,
    layeringMode: TLayeringMode,
    yFn: ScaleLinear<number, number>
  ) => JSX.Element;

  constructor(options: LayeredReactWrapperViewOptions<TLayeringMode, M, LayoutName>) {
    super(options);
  }

  initialize(options: LayeredReactWrapperViewOptions<TLayeringMode, M, LayoutName>): void {
    super.initialize(options);
    this.layeringMode = options.layeringMode;
    this.singleHeight = options.singleHeight;
    this.getSliceModels = options.getSliceModels;
    this.renderReact = options.renderReact;
  }

  /**
   * A LayersDict maps the index of a model within sliceModels to the index of the layer it should be on.
   */
  getLayersDict(): LayersDict {
    const sliceModels = this.getSliceModels();
    return getLayersDict(sliceModels, this.layeringMode);
  }

  /**
   * Calculates how many horizontal layers this slice needs.
   */
  numLayers(): number {
    return Object.keys(this.getLayersDict()).length;
  }

  /**
   * Makes a d3 ScaleLinear that calculates the y position of a feature, given its y-index.
   */
  getYFn(): ScaleLinear<number, number> {
    const overlaps = this.numLayers();
    return d3
      .scaleLinear()
      .domain([0, overlaps])
      .range([0, overlaps * (this.singleHeight() + this.getSingleVMargin())]);
  }

  boxHeight(): number {
    const numLayers = this.numLayers();
    if (!this.shouldShow() || numLayers === 0) {
      return 0;
    } else {
      return numLayers * (this.singleHeight() + this.getSingleVMargin()) + this.getLayout().vMargin;
    }
  }

  getSingleVMargin(): number {
    return (this.getLayout() as PolymerLayout).vMarginSingle ?? 0;
  }

  redraw(): void {
    this.trigger('redraw');
  }

  _renderComponentWrapper(): JSX.Element {
    return this.renderReact(
      this.getLayout(),
      this.redraw.bind(this),
      this.getSliceModels(),
      this.getLayersDict(),
      this.layeringMode,
      this.getYFn()
    );
  }
}
